import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Building2, Mail, MapPin, Phone } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { C, COMPANY, IMAGES } from "@/data/content";
import Reveal, { SectionHeader } from "@/components/Reveal";

export default function About() {
    const { L } = useLanguage();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], [-18, 18]);

    const info = [
        { icon: MapPin, label: L(C.about.addrLabel), value: L(COMPANY.address), testid: "about-hq-address" },
        { icon: Building2, label: L(C.about.branchesLabel), value: L(C.about.branches), testid: "about-branches" },
        { icon: Mail, label: "Email", value: COMPANY.email, href: COMPANY.emailHref, testid: "about-hq-email" },
        ...COMPANY.phones.map((p, i) => ({
            icon: Phone,
            label: L(C.about.phoneLabel),
            value: p.display,
            href: p.href,
            testid: `about-hq-phone-${i + 1}`,
        })),
    ];

    return (
        <section id="about" ref={ref} className="relative py-24 sm:py-32">
            <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12">
                <div className="lg:col-span-7">
                    <SectionHeader
                        eyebrow={L(C.about.eyebrow)}
                        title={L(C.about.title)}
                    />
                    <Reveal delay={0.1}>
                        <p className="font-display mt-9 text-xl leading-relaxed text-brass-bright sm:text-2xl">
                            {L(C.about.statement)}
                        </p>
                    </Reveal>
                    <Reveal delay={0.16}>
                        <p className="mt-6 leading-relaxed text-txt-2">
                            {L(C.about.p1)}
                        </p>
                    </Reveal>
                    <Reveal delay={0.22}>
                        <p className="mt-4 leading-relaxed text-txt-2">
                            {L(C.about.p2)}
                        </p>
                    </Reveal>
                </div>

                <div className="lg:col-span-5">
                    <Reveal delay={0.1}>
                        <div className="relative">
                            <div className="absolute inset-0 translate-y-4 rounded-2xl border border-gold-line-strong ltr:translate-x-4 rtl:-translate-x-4" />
                            <div className="relative overflow-hidden rounded-2xl">
                                <motion.img
                                    style={{ y: imgY }}
                                    src={IMAGES.gz}
                                    alt="Guangzhou skyline"
                                    className="h-[420px] w-full scale-110 object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
                                <span className="font-mono-x absolute bottom-4 start-4 text-[10px] tracking-[0.3em] text-brass-bright">
                                    {C.about.caption}
                                </span>
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.2}>
                        <div
                            data-testid="about-hq-card"
                            className="glass mt-8 rounded-2xl border border-gold-line p-6"
                        >
                            <p className="font-mono-x text-[10px] tracking-[0.3em] text-brass uppercase">
                                {L(C.about.hqTitle)}
                            </p>
                            <ul className="mt-4 space-y-3">
                                {info.map(({ icon: Icon, label, value, href, testid }) => (
                                    <li
                                        key={testid}
                                        data-testid={testid}
                                        className="flex items-center gap-3 text-sm"
                                    >
                                        <Icon className="size-4 shrink-0 text-brass" />
                                        <span className="shrink-0 text-txt-3">{label}:</span>
                                        {href ? (
                                            <a href={href} dir="ltr" className="text-txt-1 transition-colors hover:text-brass">
                                                {value}
                                            </a>
                                        ) : (
                                            <span dir="auto" className="text-txt-1">
                                                {value}
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
